// Datos de Excepciones. Las cinco mutaciones invalidan la lista y la ficha: cambian el estado o la
// bitácora, así que dejar el caché viejo mostraría una decisión que ya se tomó como pendiente.
//
// El ciclo completo es NEW → IN_REVIEW → APPROVED/REJECTED → APPLIED. El front solo cableaba
// `resolve`, así que una excepción aprobada se quedaba para siempre en `APPROVED` y la bandeja no
// distinguía lo pendiente de lo hecho — con los endpoints ya desplegados del otro lado.
import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    exceptionsApi,
    type CreateExceptionRequest,
    type ResolveExceptionRequest,
} from '@/api/services/retail-wizard/backoffice';
import { storeKpisApi } from '@/api/services/retail-org/backoffice';
import { listRetailUsers, listStores } from '@/api/services/retail-org/retail-org.client';

const KEY = ['retail-admin', 'excepciones'] as const;

/**
 * Los dos padrones que la pantalla necesita para poner NOMBRES donde el silo solo manda ULIDs: las
 * tiendas y los usuarios retail de la cadena.
 *
 * 🔴 La respuesta de excepciones trae `storeId` y `requestedBy` y nada más — no existe `storeName`
 * ni `requestedByName` con valor en ningún lado del backend (`requestedByName` viaja `null` en las
 * 229 filas). Sin este cruce la tabla pinta ULIDs crudos en dos de sus ocho columnas.
 *
 * 🔴 EL ALTA SOLO ALCANZABA 8 DE LAS 12 TIENDAS. El único padrón cableado era
 * `GET /backoffice/stores/kpis`, que tiene eje en la VENTA: la tienda que no vendió en la ventana
 * no aparece, y por eso no se podía levantar una excepción sobre ella. `retail-org` sí devuelve las
 * 12 con el mismo rol. Se leen LOS DOS y se unen: la unión nunca es más pobre que cualquiera de los
 * dos por separado, así que si `retail-org` responde 403 el formulario queda como estaba y no peor.
 *
 * Va con `allSettled`: un padrón caído NO tumba la bandeja — la pantalla cae a mostrar los ids.
 */
export function useRetailDirectory(retailerId: string | null) {
    const query = useQuery({
        queryKey: [...KEY, 'directory', retailerId],
        staleTime: 300_000,
        queryFn: async () => {
            const [kpis, orgStores, orgUsers] = await Promise.allSettled([
                storeKpisApi.listStores(),
                retailerId === null ? Promise.resolve([]) : listStores(retailerId),
                retailerId === null ? Promise.resolve([]) : listRetailUsers(retailerId),
            ]);
            return {
                kpiStores: kpis.status === 'fulfilled' ? kpis.value.items : [],
                orgStores: orgStores.status === 'fulfilled' ? orgStores.value : [],
                orgUsers: orgUsers.status === 'fulfilled' ? orgUsers.value : [],
                // Solo se avisa cuando NINGUNA de las dos fuentes de tiendas respondió: con una de
                // las dos el selector sigue siendo utilizable.
                storesUnavailable: kpis.status === 'rejected' && orgStores.status === 'rejected',
            };
        },
    });

    const storeNames = useMemo(() => {
        const map = new Map<string, string>();
        for (const store of query.data?.kpiStores ?? []) map.set(store.storeId, store.name);
        // `retail-org` es el padrón; pisa al de KPIs cuando los dos traen la misma tienda.
        for (const store of query.data?.orgStores ?? []) map.set(store.storeId, store.name);
        return map;
    }, [query.data]);

    const userNames = useMemo(() => {
        const map = new Map<string, string>();
        for (const user of query.data?.orgUsers ?? []) {
            // `displayName` es opcional (DEC-SKF1-021): sin él no hay nombre que mostrar y el id
            // crudo es mejor que una cadena vacía.
            if (user.displayName) map.set(user.retailUserId, user.displayName);
        }
        return map;
    }, [query.data]);

    const storeNameOf = useCallback(
        (storeId: string | null) => (storeId ? (storeNames.get(storeId) ?? null) : null),
        [storeNames],
    );
    const userNameOf = useCallback(
        (userId: string | null) => (userId ? (userNames.get(userId) ?? null) : null),
        [userNames],
    );

    /** Las tiendas del ALTA: el padrón completo, alfabético. */
    const storeOptions = useMemo(
        () =>
            [...storeNames.entries()]
                .map(([value, label]) => ({ value, label }))
                .sort((a, b) => a.label.localeCompare(b.label, 'es-MX')),
        [storeNames],
    );

    return {
        /** `null` cuando no se pudo resolver: la UI muestra el id crudo, no un hueco. */
        storeNameOf,
        userNameOf,
        storeOptions,
        isPending: query.isPending,
        storesUnavailable: query.data?.storesUnavailable ?? false,
    };
}

export function useExcepciones() {
    return useQuery({
        queryKey: [...KEY, 'list'],
        queryFn: () => exceptionsApi.listExceptions(),
        staleTime: 30_000,
    });
}

export function useExcepcionDetail(exceptionId: string | null) {
    return useQuery({
        queryKey: [...KEY, 'detail', exceptionId],
        queryFn: () => exceptionsApi.getException(exceptionId ?? ''),
        enabled: exceptionId !== null,
        staleTime: 30_000,
    });
}

export function useCreateExcepcion() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (request: CreateExceptionRequest) => exceptionsApi.createException(request),
        // `onSettled`, no `onSuccess`: tras un 409 la ficha se quedaba con el estado VIEJO y el
        // botón seguía habilitado, así que el operador podía reintentar sin fin contra un caso que
        // ya no admite esa transición. Los tres errores del silo documentan «el front debe recargar
        // la ficha» — recargar también al fallar es justamente cuando más hace falta.
        onSettled: () => {
            void qc.invalidateQueries({ queryKey: KEY });
        },
    });
}

export function useResolveExcepcion() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({
            exceptionId,
            request,
        }: {
            exceptionId: string;
            request: ResolveExceptionRequest;
        }) => exceptionsApi.resolveException(exceptionId, request),
        // `onSettled`, no `onSuccess`: tras un 409 la ficha se quedaba con el estado VIEJO y el
        // botón seguía habilitado, así que el operador podía reintentar sin fin contra un caso que
        // ya no admite esa transición. Los tres errores del silo documentan «el front debe recargar
        // la ficha» — recargar también al fallar es justamente cuando más hace falta.
        onSettled: () => {
            void qc.invalidateQueries({ queryKey: KEY });
        },
    });
}

/** Tomar el caso (`NEW → IN_REVIEW`) — para que dos Admin VL no trabajen el mismo. */
export function useReviewExcepcion() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ exceptionId, note }: { exceptionId: string; note?: string }) =>
            exceptionsApi.reviewException(exceptionId, note),
        // `onSettled`, no `onSuccess`: tras un 409 la ficha se quedaba con el estado VIEJO y el
        // botón seguía habilitado, así que el operador podía reintentar sin fin contra un caso que
        // ya no admite esa transición. Los tres errores del silo documentan «el front debe recargar
        // la ficha» — recargar también al fallar es justamente cuando más hace falta.
        onSettled: () => {
            void qc.invalidateQueries({ queryKey: KEY });
        },
    });
}

/** Marcar como ejecutada (`APPROVED → APPLIED`). NO muta la venta ni re-liquida comisión. */
export function useApplyExcepcion() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ exceptionId, note }: { exceptionId: string; note?: string }) =>
            exceptionsApi.applyException(exceptionId, note),
        // `onSettled`, no `onSuccess`: tras un 409 la ficha se quedaba con el estado VIEJO y el
        // botón seguía habilitado, así que el operador podía reintentar sin fin contra un caso que
        // ya no admite esa transición. Los tres errores del silo documentan «el front debe recargar
        // la ficha» — recargar también al fallar es justamente cuando más hace falta.
        onSettled: () => {
            void qc.invalidateQueries({ queryKey: KEY });
        },
    });
}

/** Una nota de seguimiento. No cambia el estado y se acepta incluso con el caso ya cerrado. */
export function useAddExcepcionNote() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ exceptionId, body }: { exceptionId: string; body: string }) =>
            exceptionsApi.addExceptionNote(exceptionId, body),
        // `onSettled`, no `onSuccess`: tras un 409 la ficha se quedaba con el estado VIEJO y el
        // botón seguía habilitado, así que el operador podía reintentar sin fin contra un caso que
        // ya no admite esa transición. Los tres errores del silo documentan «el front debe recargar
        // la ficha» — recargar también al fallar es justamente cuando más hace falta.
        onSettled: () => {
            void qc.invalidateQueries({ queryKey: KEY });
        },
    });
}
